import { useState } from "react";

import {
  Box,
  Button,
  Center,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue
} from "@chakra-ui/react";

import { ExternalLinkIcon } from "@chakra-ui/icons";

import { DataType } from "@/jenesys-types/data/enums";
import {
  ClientTransactions,
  statusStyles
} from "@/jenesys-types/jenesys-entities/config";

import { Paginate } from "@/components/common/table/Paginate";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";
import { useCompany } from "@/hooks/util/useCompany";
import { CrudRouteType, getCrudRouteName } from "@/routes/crud";

interface ClientTransactionReportProps {
  reports: ClientTransactions;
  isClientTransactionsLoading: boolean;
  onPageChange?: (page: number) => void;
}

export const ClientTransactionReport: React.FC<
  ClientTransactionReportProps
> = ({ reports, isClientTransactionsLoading, onPageChange }) => {
  const [page, setPage] = useState(0);

  const formattedDate = (date: Date) =>
    new Intl.DateTimeFormat("en-GB", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }).format(date);

  const { activeClientId } = useCompany();
  const navigate = useNamedNavigate();

  // Responsive column visibility
  const showDateColumn = useBreakpointValue({ base: false, md: true });
  const showSupplierColumn = useBreakpointValue({ base: false, lg: true });
  const showProcessingTimeColumn = useBreakpointValue({
    base: false,
    xl: true
  });

  const handleViewDocument = (documentId: string) => {
    navigate(getCrudRouteName(DataType.Transaction, CrudRouteType.Edit), {
      clientId: activeClientId,
      transactionId: documentId
    });
  };

  const handlePageClick = (event: { selected: number }) => {
    setPage(event.selected);
    if (onPageChange) {
      onPageChange(event.selected);
    }
  };

  if (isClientTransactionsLoading) {
    return (
      <Center py={8}>
        <Spinner color="primary.500" />
      </Center>
    );
  }

  // Calculate dynamic column count for empty state
  const getColumnCount = () => {
    let count = 2; // Document Number + View button (always visible)
    if (showDateColumn) count++;
    if (showSupplierColumn) count++;
    if (showProcessingTimeColumn) count++;
    return count + 1; // +1 for Status column
  };

  return (
    <Box overflowX="auto">
      <TableContainer>
        <Table variant="unstyled" size="sm" minW="800px" layout="fixed">
          <Thead>
            <Tr>
              {showDateColumn && (
                <Th
                  color="blackAlpha.700"
                  textTransform="capitalize"
                  fontWeight={500}
                  minW="140px"
                  w="140px"
                >
                  Date Extracted
                </Th>
              )}
              <Th
                color="blackAlpha.700"
                textTransform="capitalize"
                fontWeight={500}
                minW="150px"
                w={showSupplierColumn ? "150px" : "200px"}
              >
                Document Number
              </Th>
              {showSupplierColumn && (
                <Th
                  color="blackAlpha.700"
                  textTransform="capitalize"
                  fontWeight={500}
                  minW="150px"
                  w="150px"
                >
                  Supplier
                </Th>
              )}
              <Th
                color="blackAlpha.700"
                textTransform="capitalize"
                fontWeight={500}
                minW="100px"
                w="100px"
              >
                Status
              </Th>
              {showProcessingTimeColumn && (
                <Th
                  color="blackAlpha.700"
                  textTransform="capitalize"
                  fontWeight={500}
                  isNumeric
                  textAlign="right"
                  minW="120px"
                  w="120px"
                >
                  Processing Time (sec)
                </Th>
              )}
              <Th
                color="blackAlpha.700"
                textTransform="capitalize"
                fontWeight={500}
                isNumeric
                minW="80px"
                w="80px"
              />
            </Tr>
          </Thead>
          <Tbody>
            {reports?.items?.map(
              ({
                document_id,
                extracted_at,
                extraction_duration,
                data,
                status,
                is_zero_time
              }) => (
                <Tr
                  key={document_id}
                  textDecoration={is_zero_time ? "line-through" : "none"}
                  opacity={is_zero_time ? 0.7 : 1}
                  title={
                    is_zero_time
                      ? "This transaction is marked as non-billable"
                      : ""
                  }
                  minH="48px" // Ensure consistent row height
                >
                  {showDateColumn && (
                    <Td
                      fontWeight={500}
                      py={3}
                      minH="48px"
                      verticalAlign="middle"
                      w="140px"
                    >
                      {extracted_at ? (
                        <Text fontSize="sm">
                          {formattedDate(new Date(extracted_at))}
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.400" fontStyle="italic">
                          —
                        </Text>
                      )}
                    </Td>
                  )}
                  <Td
                    fontWeight={500}
                    py={3}
                    minH="48px"
                    verticalAlign="middle"
                    w={showSupplierColumn ? "150px" : "200px"}
                  >
                    {data?.invoice_id ? (
                      <Tooltip label={data.invoice_id} placement="top">
                        <Text noOfLines={1} fontSize="sm" maxW="100%">
                          {data.invoice_id}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text fontSize="sm" color="gray.400" fontStyle="italic">
                        —
                      </Text>
                    )}
                  </Td>
                  {showSupplierColumn && (
                    <Td
                      fontWeight={500}
                      py={3}
                      minH="48px"
                      verticalAlign="middle"
                      w="150px"
                    >
                      {data?.supplier_name ? (
                        <Tooltip label={data.supplier_name} placement="top">
                          <Text noOfLines={1} fontSize="sm" maxW="100%">
                            {data.supplier_name}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text fontSize="sm" color="gray.400" fontStyle="italic">
                          —
                        </Text>
                      )}
                    </Td>
                  )}
                  <Td
                    fontWeight={500}
                    py={3}
                    minH="48px"
                    verticalAlign="middle"
                    w="100px"
                  >
                    <Center
                      rounded="full"
                      px={3}
                      w="fit-content"
                      fontWeight={600}
                      py={2}
                      fontSize={12}
                      bg={statusStyles[status as keyof typeof statusStyles]?.bg}
                      color={
                        statusStyles[status as keyof typeof statusStyles]?.color
                      }
                      minH="28px" // Ensure consistent badge height
                    >
                      {is_zero_time
                        ? "Non-Billable"
                        : statusStyles[status as keyof typeof statusStyles]
                            ?.text}
                    </Center>
                  </Td>
                  {showProcessingTimeColumn && (
                    <Td
                      textAlign="right"
                      fontWeight={500}
                      py={3}
                      isNumeric
                      minH="48px"
                      verticalAlign="middle"
                      w="120px"
                    >
                      <Text fontSize="sm" textAlign="right">
                        {extraction_duration && extraction_duration > 0 ? (
                          extraction_duration.toFixed(0)
                        ) : (
                          <Text as="span" color="gray.400" fontStyle="italic">
                            —
                          </Text>
                        )}
                      </Text>
                    </Td>
                  )}
                  <Td
                    fontWeight={500}
                    py={3}
                    isNumeric
                    minH="48px"
                    verticalAlign="middle"
                    w="80px"
                  >
                    <Button
                      onClick={() => handleViewDocument(document_id)}
                      rightIcon={<ExternalLinkIcon />}
                      variant="ghost"
                      colorScheme="default"
                      color="purple.600"
                      size="sm"
                      minH="32px" // Ensure consistent button height
                    >
                      View
                    </Button>
                  </Td>
                </Tr>
              )
            )}
            {/* Show empty state if no data */}
            {(!reports?.items || reports.items.length === 0) &&
              !isClientTransactionsLoading && (
                <Tr>
                  <Td
                    colSpan={getColumnCount()}
                    textAlign="center"
                    py={8}
                    color="gray.500"
                    fontStyle="italic"
                  >
                    <Text fontSize="sm">
                      No transactions found for the selected period
                    </Text>
                  </Td>
                </Tr>
              )}
          </Tbody>
        </Table>
      </TableContainer>

      {reports?.items && reports.items.length > 0 && (
        <Box mt={4} display="flex" justifyContent="center">
          <Paginate
            page={page}
            data={reports}
            handlePageClick={handlePageClick}
          />
        </Box>
      )}
    </Box>
  );
};
